// Text helpers for the setup screen: module labels, session lengths, the
// summary strip and validation problems in the player's language. Pure
// (they take a Translate), so they're unit-tested without React.

import { type AiProfile, type ModuleCategory, type ModuleId, presetById } from '@aod/protocol';
import type { Locale, MessageKey, Translate } from '../i18n/index.js';
import { type PresetDefId, type SetupDraft, estimatedMinutes, isCustomised } from './setupModel.js';

// Template-literal keys: a module or category without a string fails the typecheck.
type ModuleKey = `setup.module.${ModuleId}`;
const moduleKey = (id: ModuleId): ModuleKey & MessageKey => `setup.module.${id}`;
const moduleDescKey = (id: ModuleId): `${ModuleKey}.desc` & MessageKey => `setup.module.${id}.desc`;
const moduleKidKey = (id: ModuleId): `${ModuleKey}.kid` & MessageKey => `setup.module.${id}.kid`;

export function moduleLabel(t: Translate, id: ModuleId): string {
  return t(moduleKey(id));
}

/** Adult description, or the reading-light one for the icons UI mode (kids). */
export function moduleDescription(t: Translate, id: ModuleId, kid: boolean): string {
  return t(kid ? moduleKidKey(id) : moduleDescKey(id));
}

export function categoryLabel(t: Translate, c: ModuleCategory): string {
  return t(`setup.cat.${c}`);
}

export function presetName(t: Translate, id: PresetDefId): string {
  return t(`setup.preset.${id}`);
}

/** "Explorer" · "Custom (based on Explorer)". */
export function draftTitle(t: Translate, d: SetupDraft): string {
  const name = presetName(t, d.basedOn);
  return isCustomised(d) ? t('setup.custom', { name }) : name;
}

/** "15–25 min", or whole hours from two hours up ("2–4 h"). */
export function formatSession(t: Translate, [lo, hi]: readonly [number, number]): string {
  if (lo >= 120 && lo % 60 === 0 && hi % 60 === 0) {
    return t('setup.sessionHours', { min: lo / 60, max: hi / 60 });
  }
  return t('setup.sessionMin', { min: lo, max: hi });
}

/**
 * "Faith crises, Sacrifice and Faith victory" in the player's language
 * (English without the serial comma, as in the rest of the UI copy).
 */
export function formatList(locale: Locale, items: readonly string[]): string {
  try {
    return new Intl.ListFormat(locale === 'en' ? 'en-GB' : locale, {
      style: 'long',
      type: 'conjunction',
    }).format(items);
  } catch {
    return items.join(', ');
  }
}

function aiPhrase(t: Translate, count: number, profile: AiProfile): string {
  const form = count === 1 ? 'one' : 'other';
  const adjective = t(`setup.summary.profile.${profile}.${form}`);
  return t(`setup.summary.ai.${form}`, { count, profile: adjective });
}

function durationPhrase(t: Translate, minutes: number): string {
  if (minutes >= 120) return t('setup.summary.hours', { hours: Math.round(minutes / 60) });
  return t('setup.summary.minutes', { min: minutes });
}

/** "Apprentice · small map · 2 easy AIs · ~35 min" (or "Custom (based on …) · …"). */
export function summaryParts(t: Translate, d: SetupDraft): string[] {
  const tuning = d.ruleset.tuning;
  return [
    draftTitle(t, d),
    t(`setup.summary.map.${tuning.mapSize}`),
    aiPhrase(t, tuning.aiOpponents, d.aiProfile),
    durationPhrase(t, estimatedMinutes(d)),
  ];
}

export function summaryText(t: Translate, d: SetupDraft): string {
  return summaryParts(t, d).join(' · ');
}

/**
 * Translate a `validateRuleset` problem. The contract returns plain English;
 * the known shapes are matched here and anything new falls through as-is.
 */
export function localizeProblem(t: Translate, problem: string): string {
  const needs = /^(\S+) needs (\S+)$/.exec(problem);
  if (needs) {
    const [, m, d] = needs;
    return t('setup.problem.needs', {
      module: moduleLabel(t, m as ModuleId),
      dependency: moduleLabel(t, d as ModuleId),
    });
  }
  const unknown = /^Unknown module: (.+)$/.exec(problem);
  if (unknown) return t('setup.problem.unknown', { id: unknown[1] ?? '' });
  const fixed: Record<string, MessageKey> = {
    'At least one way to win is needed': 'setup.problem.noVictory',
    'Family content rating excludes mature themes': 'setup.problem.family',
    'AI opponents must be 1–7': 'setup.problem.aiRange',
    'Peace period must be 0–20 min': 'setup.problem.peaceRange',
    'Truce must be 0–5 min': 'setup.problem.truceRange',
  };
  const key = fixed[problem];
  return key ? t(key) : problem;
}

/** Session range of the preset a ruleset is based on. */
export function sessionOf(id: PresetDefId): readonly [number, number] {
  return presetById(id).sessionMin;
}
